// src/store/modules/newTipV2.js
//
// Module Vuex du back-office New TIP v2. Même structure que le module
// newTip (v1), avec les différences propres à la v2 :
//   - la liste des produits (une campagne = un produit)
//   - le filtre dashboard `achieved_tier`
//
// Enregistrement (dans store/index.js) :
//   import newTipV2 from './modules/newTipV2';
//   export default new Vuex.Store({ modules: { newTipV2 } });

import api from '@/api/newTipV2Api';

const initialState = () => ({
  // Campagnes
  campaigns: [],
  campaignsMeta: { current_page: 1, last_page: 1, total: 0 },
  campaignFilters: { status: null, search: '', product_id: null },
  currentCampaign: null,

  // Référentiel produits (chargé une fois, mis en cache)
  products: [],

  // Affectations
  assignments: [],
  assignmentsMeta: { current_page: 1, last_page: 1, total: 0 },

  // Dashboard
  dashboard: {
    kpis: null,
    visits: [],
    meta: { current_page: 1, last_page: 1, total: 0 },
  },
  dashboardFilters: {
    campaign_id: null,
    supervisor_id: null,
    salepoint_id: null,
    salepoint_code: null,
    status: null,
    achieved_tier: null,
    date_from: null,
    date_to: null,
  },

  // Visite courante (review)
  currentVisit: null,

  loading: {
    campaigns: false,
    assignments: false,
    dashboard: false,
    visit: false,
    products: false,
    saving: false,
  },
  error: null,
});

const state = initialState();

const mutations = {
  RESET(state) { Object.assign(state, initialState()); },

  SET_LOADING(state, { key, value }) { state.loading[key] = value; },
  SET_ERROR(state, error) { state.error = error; },

  SET_CAMPAIGNS(state, { data, meta }) {
    state.campaigns = data;
    state.campaignsMeta = meta;
  },
  SET_CAMPAIGN_FILTERS(state, filters) {
    state.campaignFilters = { ...state.campaignFilters, ...filters };
  },
  SET_CURRENT_CAMPAIGN(state, campaign) { state.currentCampaign = campaign; },
  ADD_CAMPAIGN(state, campaign) { state.campaigns.unshift(campaign); },
  UPDATE_CAMPAIGN(state, updated) {
    const idx = state.campaigns.findIndex(c => c.id === updated.id);
    if (idx !== -1) state.campaigns.splice(idx, 1, updated);
    if (state.currentCampaign && state.currentCampaign.id === updated.id) {
      state.currentCampaign = updated;
    }
  },
  REMOVE_CAMPAIGN(state, id) {
    state.campaigns = state.campaigns.filter(c => c.id !== id);
  },

  SET_PRODUCTS(state, products) { state.products = products; },

  SET_ASSIGNMENTS(state, { data, meta }) {
    state.assignments = data;
    state.assignmentsMeta = meta;
  },

  SET_DASHBOARD(state, { kpis, visits }) {
    state.dashboard.kpis = kpis;
    state.dashboard.visits = visits.data;
    state.dashboard.meta = {
      current_page: visits.current_page,
      last_page: visits.last_page,
      total: visits.total,
    };
  },
  SET_DASHBOARD_FILTERS(state, filters) {
    state.dashboardFilters = { ...state.dashboardFilters, ...filters };
  },
  RESET_DASHBOARD_FILTERS(state) {
    state.dashboardFilters = initialState().dashboardFilters;
  },

  SET_CURRENT_VISIT(state, visit) { state.currentVisit = visit; },
};

const actions = {
  // ---- Campagnes -------------------------------------------------------
  async fetchCampaigns({ commit, state }, params = {}) {
    commit('SET_LOADING', { key: 'campaigns', value: true });
    commit('SET_ERROR', null);
    try {
      const merged = { ...state.campaignFilters, ...params };
      const { data } = await api.listCampaigns(merged);
      commit('SET_CAMPAIGNS', {
        data: data.data,
        meta: { current_page: data.current_page, last_page: data.last_page, total: data.total },
      });
    } catch (err) {
      commit('SET_ERROR', err);
      throw err;
    } finally {
      commit('SET_LOADING', { key: 'campaigns', value: false });
    }
  },

  async fetchCampaign({ commit }, id) {
    commit('SET_LOADING', { key: 'campaigns', value: true });
    try {
      const { data } = await api.showCampaign(id);
      commit('SET_CURRENT_CAMPAIGN', data.campaign);
      return data.campaign;
    } finally {
      commit('SET_LOADING', { key: 'campaigns', value: false });
    }
  },

  async createCampaign({ commit }, payload) {
    commit('SET_LOADING', { key: 'saving', value: true });
    try {
      const { data } = await api.createCampaign(payload);
      commit('ADD_CAMPAIGN', data.campaign);
      return data.campaign;
    } finally {
      commit('SET_LOADING', { key: 'saving', value: false });
    }
  },

  async updateCampaign({ commit }, { id, payload }) {
    commit('SET_LOADING', { key: 'saving', value: true });
    try {
      const { data } = await api.updateCampaign(id, payload);
      commit('UPDATE_CAMPAIGN', data.campaign);
      return data.campaign;
    } finally {
      commit('SET_LOADING', { key: 'saving', value: false });
    }
  },

  async deleteCampaign({ commit }, id) {
    await api.deleteCampaign(id);
    commit('REMOVE_CAMPAIGN', id);
  },

  // ---- Produits --------------------------------------------------------
  // Chargés une seule fois : le référentiel bouge peu et le sélecteur de
  // produit est utilisé sur plusieurs écrans.
  async fetchProducts({ commit, state }, force = false) {
    if (state.products.length && !force) return state.products;

    commit('SET_LOADING', { key: 'products', value: true });
    try {
      const { data } = await api.listProducts();
      const products = data.data || data || [];
      commit('SET_PRODUCTS', products);
      return products;
    } finally {
      commit('SET_LOADING', { key: 'products', value: false });
    }
  },

  // ---- Affectations ----------------------------------------------------
  async fetchAssignments({ commit }, { campaignId, params = {} }) {
    commit('SET_LOADING', { key: 'assignments', value: true });
    try {
      const { data } = await api.listAssignments(campaignId, params);
      commit('SET_ASSIGNMENTS', {
        data: data.assignments.data,
        meta: {
          current_page: data.assignments.current_page,
          last_page: data.assignments.last_page,
          total: data.assignments.total,
        },
      });
      commit('SET_CURRENT_CAMPAIGN', data.campaign);
    } finally {
      commit('SET_LOADING', { key: 'assignments', value: false });
    }
  },

  async saveAssignments({ commit }, { campaignId, assignments }) {
    commit('SET_LOADING', { key: 'saving', value: true });
    try {
      const { data } = await api.storeAssignmentsBulk(campaignId, assignments);
      return data;
    } finally {
      commit('SET_LOADING', { key: 'saving', value: false });
    }
  },

  async importAssignments({ commit }, { campaignId, file, onProgress }) {
    commit('SET_LOADING', { key: 'saving', value: true });
    try {
      const { data } = await api.importAssignments(campaignId, file, onProgress);
      return data;
    } finally {
      commit('SET_LOADING', { key: 'saving', value: false });
    }
  },

  async deleteAssignment(_, id) {
    await api.deleteAssignment(id);
  },

  // ---- Dashboard -------------------------------------------------------
  async fetchDashboard({ commit, state }, override = {}) {
    commit('SET_LOADING', { key: 'dashboard', value: true });
    try {
      const filters = { ...state.dashboardFilters, ...override };
      const { data } = await api.dashboard(filters);
      commit('SET_DASHBOARD', { kpis: data.kpis, visits: data.visits });
    } finally {
      commit('SET_LOADING', { key: 'dashboard', value: false });
    }
  },

  async exportDashboard({ state }) {
    const { data, headers } = await api.exportDashboard(state.dashboardFilters);
    const blob = new Blob([data], { type: headers['content-type'] });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `new-tip-v2-dashboard-${Date.now()}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  // ---- Visites ---------------------------------------------------------
  async fetchVisit({ commit }, id) {
    commit('SET_LOADING', { key: 'visit', value: true });
    try {
      const { data } = await api.showVisit(id);
      commit('SET_CURRENT_VISIT', data.visit);
      return data.visit;
    } finally {
      commit('SET_LOADING', { key: 'visit', value: false });
    }
  },

  async reviewVisit({ commit }, { id, action, rejectionReason = null }) {
    commit('SET_LOADING', { key: 'saving', value: true });
    try {
      const { data } = await api.reviewVisit(id, action, rejectionReason);
      commit('SET_CURRENT_VISIT', data.visit);
      return data.visit;
    } finally {
      commit('SET_LOADING', { key: 'saving', value: false });
    }
  },
};

const getters = {
  isLoading: state => key => !!state.loading[key],
  hasError: state => !!state.error,
  campaignById: state => id => state.campaigns.find(c => c.id === id),
  productById: state => id => state.products.find(p => p.id === id),
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
